import {
  unstable_defaultSxConfig as defaultSxConfig,
  type SystemProps,
} from "@mui/system";
import type { SxProps, Theme } from "@mui/material/styles";

// MUI v9 removed system style props (mt, gap, flexDirection, width, …) from Box/Typography/Stack.
// This is MUI's own canonical registry of those names, used to re-route them into `sx` on wrappers.
const SYSTEM_PROP_KEYS = new Set(Object.keys(defaultSxConfig));

// A component's `color`/`bgcolor` PROP resolved a bare palette token (e.g. "primary") to its
// `.main` shade; in `sx` a bare token resolves to the whole palette object, so map token -> `.main`.
const PALETTE_MAIN_TOKENS = new Set([
  "primary",
  "secondary",
  "error",
  "warning",
  "info",
  "success",
]);
const PALETTE_COLOR_KEYS = new Set([
  "color",
  "bgcolor",
  "backgroundColor",
  "borderColor",
  "borderTopColor",
  "borderRightColor",
  "borderBottomColor",
  "borderLeftColor",
  "outlineColor",
]);

const toSxValue = (key: string, value: unknown): unknown =>
  PALETTE_COLOR_KEYS.has(key) &&
  typeof value === "string" &&
  PALETTE_MAIN_TOKENS.has(value)
    ? `${value}.main`
    : value;

type SystemStyleKey = keyof SystemProps<Theme>;

/** Split MUI v9-removed system style props off a props object so a wrapper can route them into `sx`. */
export const splitSystemProps = <T extends object>(
  props: T
): { systemSx: SystemProps<Theme>; rest: Omit<T, SystemStyleKey> } => {
  const systemSx: Record<string, unknown> = {};
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (SYSTEM_PROP_KEYS.has(key)) {
      systemSx[key] = toSxValue(key, value);
    } else {
      rest[key] = value;
    }
  }
  return {
    systemSx: systemSx as SystemProps<Theme>,
    rest: rest as Omit<T, SystemStyleKey>,
  };
};

/** Flatten and merge any number of `sx` values (object, array, or undefined) into a single `sx`. */
export const mergeSx = (
  ...styles: Array<SxProps<Theme> | undefined>
): SxProps<Theme> =>
  styles
    .flatMap((style) => (Array.isArray(style) ? style : [style]))
    .filter(Boolean) as SxProps<Theme>;
